import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Pro or Fun — how the saved moment and the refresh buttons carry themselves.
 *
 *   Pro (the default): the green moment and the indicator only.
 *   Fun: full moments get a character, the stamp or the safe, and a finished
 *        refresh throws three green sparks. Ticks look the same in both.
 *
 * Per user and synced across devices through the preferences API
 * (`personality`); see usePersonalitySync. It is also persisted here so the
 * choice is right on the first frame, before preferences have loaded.
 *
 * Deliberately tiny with no imports beyond zustand: the refresh button reads
 * it, and that button sits in surfaces all over the app.
 *
 * Design: lilia-docs/design-handoffs/2026-09-27-saved-moment/ §4.
 */
export type Personality = "pro" | "fun";

export const DEFAULT_PERSONALITY: Personality = "pro";

export function normalizePersonality(value: unknown): Personality | null {
  if (typeof value !== "string") return null;
  const v = value.toLowerCase();
  return v === "pro" || v === "fun" ? v : null;
}

interface PersonalityState {
  personality: Personality;
  /** Set when the person chose in this session, so a late server value
   *  arriving afterwards does not quietly undo their choice. */
  chosenLocally: boolean;
  setPersonality: (p: Personality) => void;
  /** Server → store, without counting as the person's own choice. */
  adoptServerPersonality: (p: Personality) => void;
}

export const usePersonalityStore = create<PersonalityState>()(
  persist(
    (set) => ({
      personality: DEFAULT_PERSONALITY,
      chosenLocally: false,
      setPersonality: (personality) => set({ personality, chosenLocally: true }),
      adoptServerPersonality: (personality) => set({ personality }),
    }),
    {
      name: "lilia.personality",
      // Only the value survives a reload; "chosen this session" must not.
      partialize: (s) => ({ personality: s.personality }),
    },
  ),
);
